import { memo, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { getEventIcon } from "../../eventIconMap";
import { formatDateTime } from "../../utils";
import SafeBoundary from "../SafeBoundary";
import EventCard from "./EventCard";
import {
  CARD_WIDTH,
  DOT_SIZE,
  HALF_HEIGHT,
  MIN_CONNECTOR_HEIGHT,
} from "./constants";

// One pulse animation per color, created once and reused.
const pulseRingCache = {};
const getPulseRing = (color) => {
  if (!pulseRingCache[color]) {
    pulseRingCache[color] = keyframes`
      0% { box-shadow: 0 0 0 0 ${alpha(color, 0.5)}; }
      70% { box-shadow: 0 0 0 8px ${alpha(color, 0)}; }
      100% { box-shadow: 0 0 0 0 ${alpha(color, 0)}; }
    `;
  }
  return pulseRingCache[color];
};

const halfSx = (justifyContent) => ({
  height: HALF_HEIGHT,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent,
});

// One timeline slot: card above/below the line, dot on the line, time on the other side.
const TimelineColumn = ({ event, isTop, isLatest }) => {
  const { Icon, color } = useMemo(
    () => getEventIcon(event),
    [event],
  );

  const card = (
    <SafeBoundary name="EventCard">
      <EventCard event={event} Icon={Icon} color={color} isLatest={isLatest} />
    </SafeBoundary>
  );
  const connector = (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: MIN_CONNECTOR_HEIGHT,
        width: 0,
        borderLeft: `2px dashed ${color}`,
      }}
    />
  );
  const timeLabel = (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        color,
        whiteSpace: "nowrap",
        my: 0.75,
        flexShrink: 0,
      }}
    >
      {formatDateTime(event.receivedAt, "hh:mm A")}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexShrink: 0,
        width: CARD_WIDTH,
      }}
    >
      <Box sx={halfSx("flex-end")}>
        {isTop ? (
          <>
            {card}
            {connector}
          </>
        ) : (
          timeLabel
        )}
      </Box>
      <Box
        sx={{
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
          zIndex: 1,
          animation: isLatest
            ? `${getPulseRing(color)} 1.8s ease-out infinite`
            : "none",
        }}
      />
      <Box sx={halfSx("flex-start")}>
        {isTop ? (
          timeLabel
        ) : (
          <>
            {connector}
            {card}
          </>
        )}
      </Box>
    </Box>
  );
};

export default memo(TimelineColumn);
